import { useState, useRef, useEffect } from 'react';
import { Input } from "@/components/ui/input";
import { Command, CommandGroup, CommandItem } from "@/components/ui/command";
import { supabase } from "@/lib/supabase";

interface WorkspaceFieldAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  workspaceId: string;
  field: 'company_name' | 'industry';
  placeholder?: string;
  className?: string;
}

export function WorkspaceFieldAutocomplete({
  value,
  onChange,
  workspaceId,
  field,
  placeholder,
  className = "h-8 text-sm",
}: WorkspaceFieldAutocompleteProps) {
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchSuggestions = async (input: string) => {
    if (!workspaceId || !input.trim()) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const { data, error } = await supabase
      .from('contacts')
      .select(field)
      .eq('workspace_id', workspaceId)
      .not(field, 'is', null)
      .ilike(field, `%${input}%`)
      .order(field)
      .limit(10);

    if (error) {
      setSuggestions([]);
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const unique = Array.from(new Set(data?.map((d: any) => d[field]).filter(Boolean))) as string[];
    const limited = unique.slice(0, 5);
    setSuggestions(limited);
    setShowSuggestions(limited.length > 0);
  };

  const handleChange = (input: string) => {
    onChange(input);
    if (input.trim()) {
      fetchSuggestions(input);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSelect = (suggestion: string) => {
    onChange(suggestion);
    setShowSuggestions(false);
  };

  return (
    <div className="relative" ref={containerRef}>
      <Input
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        onFocus={() => value.trim() && fetchSuggestions(value)}
        placeholder={placeholder}
        className={className}
      />

      {showSuggestions && suggestions.length > 0 && (
        <Command className="absolute z-50 w-full mt-1 border rounded-md shadow-md">
          <CommandGroup>
            {suggestions.map((suggestion) => (
              <CommandItem
                key={suggestion}
                onSelect={() => handleSelect(suggestion)}
                className="text-sm"
              >
                {suggestion}
              </CommandItem>
            ))}
          </CommandGroup>
        </Command>
      )}
    </div>
  );
}
